import moment from "moment"

import { theme } from "@common/theme/theme"

export const DATETIME_FORMAT = "YYYY-MM-DD HH:mm"

export const HOUR_OPTIONS = [1, 2, 3]

// Indicative price per hour by format. The backend owns these rates
// (`ExtraHourRequest::PRICE_PER_HOUR`) and serves them as
// `staticData.extra_hour_price_per_hour`; the values below are only the
// OFFLINE FALLBACK for when staticData hasn't loaded. Deliberately NOT
// exported — reading it directly would freeze the app on a stale rate, so
// `resolveExtraHourPricePerHour` is the only way in.
const PRICE_PER_HOUR_FALLBACK = {
    shared: 14,
    individual: 25,
}

/**
 * €/h for a format, backend grid first, offline mirror second.
 *
 * @param staticGrid - `staticData.extra_hour_price_per_hour` (may be undefined)
 * @param format     - "shared" | "individual"
 */
export const resolveExtraHourPricePerHour = (staticGrid, format) => {
    const fromBackend = staticGrid?.[format]
    if (Number.isFinite(fromBackend) && fromBackend > 0) return fromBackend

    return PRICE_PER_HOUR_FALLBACK[format] ?? 0
}

export const DEADLINE_OPTIONS = [
    { value: "3_days", label: "Sous 3 jours" },
    { value: "7_days", label: "Sous 7 jours" },
    { value: "custom", label: "Date précise" },
]

/**
 * Format cards for the parameters step. A function rather than a constant so the
 * displayed €/h comes from the backend grid (see
 * `resolveExtraHourPricePerHour`) instead of being frozen at import time.
 *
 * @param staticGrid - `staticData.extra_hour_price_per_hour` (may be undefined)
 */
export const formatOptions = (staticGrid) => [
    {
        value: "shared",
        title: "Cours partagé",
        price: `${resolveExtraHourPricePerHour(staticGrid, "shared")}€/h`,
        subtitle: "Planification rapide",
        icon: "users",
    },
    {
        value: "individual",
        title: "Cours individuel",
        price: `${resolveExtraHourPricePerHour(staticGrid, "individual")}€/h`,
        subtitle: "Tuteur 100% dédié, horaire flexible",
        icon: "user",
    },
]

export const DAY_OF_WEEK_BY_LABEL = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
}

const DAY_LABEL_BY_INT = {
    0: "Sunday",
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
}

export const FRENCH_DAY_BY_INT = {
    0: "Dimanche",
    1: "Lundi",
    2: "Mardi",
    3: "Mercredi",
    4: "Jeudi",
    5: "Vendredi",
    6: "Samedi",
}

// English day names reachable between tomorrow and the deadline window —
// the matcher rejects any occurrence past `ticket.deadline`, so a slot
// on a weekday that doesn't fall inside that window can never produce a
// match (e.g. picking "Vendredi 18h" with "sous 3 jours" on a Sunday).
// Custom deadlines pivot on the picked date; if no date yet, fall back
// to "no restriction" so the grid stays usable while the parent decides.
export const computeAllowedDays = (deadlineType, customDeadline) => {
    const start = moment().add(1, "day").startOf("day")
    let end
    if (deadlineType === "3_days") end = moment().add(3, "days").endOf("day")
    else if (deadlineType === "7_days")
        end = moment().add(7, "days").endOf("day")
    else if (deadlineType === "custom" && customDeadline)
        end = moment(customDeadline).endOf("day")
    else return null

    const allowed = new Set()
    const cur = start.clone()
    while (cur.isSameOrBefore(end, "day") && allowed.size < 7) {
        allowed.add(DAY_LABEL_BY_INT[cur.day()])
        cur.add(1, "day")
    }
    return allowed
}

export const slotStringToApi = (slot) => {
    const [day, range] = slot.split(" ")
    const day_of_week = DAY_OF_WEEK_BY_LABEL[day]
    const hour = parseInt(range?.split("h")[0], 10)
    if (day_of_week === undefined || Number.isNaN(hour)) return null
    return { day_of_week, hour }
}

// Collapse a sorted list of hours into comma-separated ranges, e.g.
// [17, 18, 19]      → "17h-19h"
// [17, 18, 20, 21]  → "17h-18h, 20h-21h"
// [17, 19]          → "17h, 19h"
export const formatHoursAsRanges = (hours) => {
    if (!hours || hours.length === 0) return ""
    const sorted = [...hours].sort((a, b) => a - b)
    const runs = []
    let start = sorted[0]
    let end = sorted[0]
    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === end + 1) {
            end = sorted[i]
        } else {
            runs.push(start === end ? `${start}h` : `${start}h-${end}h`)
            start = sorted[i]
            end = sorted[i]
        }
    }
    runs.push(start === end ? `${start}h` : `${start}h-${end}h`)
    return runs.join(", ")
}

// Local validation mirrored from `StoreExtraHourRequestRequest` on the
// backend. Surfaced on the recap step so the parent sees the reason
// before the request fires (each individual step also gates its own
// "Continuer", so most cases never get this far — this is the safety
// net for any state the steps couldn't catch on their own).
export const validateForm = (values) => {
    const errors = []
    const hours = values?.hours
    const format = values?.format
    const deadlineType = values?.deadline_type
    const customDeadline = values?.custom_deadline
    const subjectId = values?.subject_id

    if (!hours || hours < 1 || hours > 3) {
        errors.push("Choisissez entre 1 et 3 heures.")
    }
    if (!format) {
        errors.push("Choisissez un format (cours partagé ou individuel).")
    }
    if (!deadlineType) {
        errors.push("Choisissez une échéance.")
    } else if (deadlineType === "custom") {
        if (!customDeadline) {
            errors.push("Choisissez une date d'échéance précise.")
        } else {
            const m = moment(customDeadline)
            const min = moment().startOf("day").add(3, "days")
            if (!m.isValid() || m.isBefore(min)) {
                errors.push(
                    "L'échéance doit être au minimum dans 3 jours (sinon utilisez le préréglage « Sous 3 jours »)."
                )
            } else if (m.isAfter(moment().add(30, "days"))) {
                errors.push("L'échéance doit être dans les 30 prochains jours.")
            }
        }
    }
    if (!subjectId) {
        errors.push("Choisissez une matière.")
    }

    if (format === "shared") {
        const slots = values?.selected_slots ?? []
        if (slots.length === 0) {
            errors.push("Sélectionnez au moins un créneau préféré.")
        }
    } else if (format === "individual" && hours > 0) {
        const dates = values?.requested_dates ?? []
        if (dates.length !== hours) {
            errors.push(
                `Sélectionnez exactement ${hours} date${hours > 1 ? "s" : ""} pour un cours individuel (1 par heure).`
            )
        } else {
            const allFutureAndValid = dates.every((v) => {
                if (typeof v !== "string") return false
                const m = moment(v, DATETIME_FORMAT, true)
                return m.isValid() && m.isAfter(moment())
            })
            if (!allFutureAndValid) {
                errors.push("Chaque date doit être valide et dans le futur.")
            }
            const unique = new Set(dates)
            if (unique.size !== dates.length) {
                errors.push("Vos dates ne peuvent pas être en double.")
            }
        }
    }

    return errors
}

export const sectionStyle = { marginTop: 20 }

// Step buttons live in a fixed footer so they stay reachable on short
// viewports — without this they sit inside the step's ScrollView and
// scroll out of frame on small screens.
export const stickyFooterStyle = {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: theme.colors.whiteAlmost,
}
